/**
 * Pure helpers for the add-employee form's "Earlier salary entries" repeater
 * (`employee_form_compensation_fields.tsx`) -- draft rows, validation and the
 * `payroll.createEmployee` `compensation_history` wire mapping. No React.
 *
 * The backend rules these mirror (`createEmployee`, `apps/api/src/trpc/
 * routers/payroll.ts`): every earlier entry has a unique date that falls
 * strictly BEFORE the current salary's effective date, and on/after the
 * employment start date when one is set; each entry carries a positive amount
 * for the employee's pay basis. Money stays in integer minor units -- the
 * `MoneyInput` in the fields component owns the decimal <-> cents conversion.
 */
import type {
  CompensationPayBasis,
  CompensationReason,
} from '../../data/use_employee_compensation.js';

/** The reasons an operator can pick for a salary entry, in dropdown order. */
export const COMPENSATION_REASONS: readonly CompensationReason[] = [
  'hire',
  'promotion',
  'annual_review',
  'correction',
  'other',
];

/** One row of the repeater. `key` is a stable React key, never sent to the backend. */
export interface CompensationHistoryDraft {
  key: string;
  /** ISO `YYYY-MM-DD`, or `''` while the operator has not picked a date yet. */
  effective_from: string;
  /** Minor units of the employee's pay basis (monthly salary or hourly rate). */
  amount_cents: number;
  reason: CompensationReason;
}

/** One entry of `payroll.createEmployee`'s `compensation_history` input. */
export interface CompensationHistoryInput {
  effective_from: string;
  pay_basis: CompensationPayBasis;
  monthly_salary_cents?: number | null;
  hourly_rate_cents?: number;
  reason: CompensationReason;
}

export type HistoryDateError =
  | 'required'
  | 'before_start'
  | 'not_before'
  | 'duplicate';

export interface HistoryRowErrors {
  date?: HistoryDateError;
  amount?: 'amount_required';
}

export interface HistoryValidationContext {
  /** Resolved effective date of the current salary (see `resolveCurrentEffectiveFrom`). */
  current_effective_from: string;
  /** `''` when the employee has no employment start date. */
  employment_start_date: string;
}

/** Today's date (UTC) as `YYYY-MM-DD` -- the backend's own "today" for the default effective date. */
export function todayIsoUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A fresh blank row with a key unique among `rows` (`h1`, `h2`, ... never reused after a removal). */
export function newHistoryRow(
  rows: readonly CompensationHistoryDraft[],
): CompensationHistoryDraft {
  let max = 0;
  for (const row of rows) {
    const n = Number(row.key.slice(1));
    if (Number.isInteger(n) && n > max) max = n;
  }
  return {
    key: `h${String(max + 1)}`,
    effective_from: '',
    amount_cents: 0,
    // The first earlier entry is almost always the starting (hire) salary.
    reason: rows.length === 0 ? 'hire' : 'other',
  };
}

/** Per-row validation errors keyed by `CompensationHistoryDraft.key`; rows without errors are absent. */
export function validateHistoryRows(
  rows: readonly CompensationHistoryDraft[],
  context: HistoryValidationContext,
): Record<string, HistoryRowErrors> {
  const dateCounts = new Map<string, number>();
  for (const row of rows) {
    if (row.effective_from !== '') {
      dateCounts.set(
        row.effective_from,
        (dateCounts.get(row.effective_from) ?? 0) + 1,
      );
    }
  }

  const result: Record<string, HistoryRowErrors> = {};
  for (const row of rows) {
    const errors: HistoryRowErrors = {};
    if (row.effective_from === '') {
      errors.date = 'required';
    } else if (
      context.employment_start_date !== '' &&
      row.effective_from < context.employment_start_date
    ) {
      errors.date = 'before_start';
    } else if (row.effective_from >= context.current_effective_from) {
      errors.date = 'not_before';
    } else if ((dateCounts.get(row.effective_from) ?? 0) > 1) {
      errors.date = 'duplicate';
    }
    if (row.amount_cents <= 0) errors.amount = 'amount_required';
    if (errors.date !== undefined || errors.amount !== undefined) {
      result[row.key] = errors;
    }
  }
  return result;
}

export function historyRowsHaveErrors(
  rows: readonly CompensationHistoryDraft[],
  context: HistoryValidationContext,
): boolean {
  return Object.keys(validateHistoryRows(rows, context)).length > 0;
}

/** Maps the draft rows to the wire shape, oldest first, using the draft's pay basis. */
export function buildCompensationHistoryInput(
  rows: readonly CompensationHistoryDraft[],
  pay_basis: CompensationPayBasis,
): CompensationHistoryInput[] {
  return [...rows]
    .sort((a, b) => a.effective_from.localeCompare(b.effective_from))
    .map((row) =>
      pay_basis === 'salaried'
        ? {
            effective_from: row.effective_from,
            pay_basis,
            monthly_salary_cents: row.amount_cents,
            reason: row.reason,
          }
        : {
            effective_from: row.effective_from,
            pay_basis,
            hourly_rate_cents: row.amount_cents,
            reason: row.reason,
          },
    );
}
